import React from 'react';
import { Briefcase, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePositions } from '@/hooks/usePositions';
import { toast } from 'sonner';

interface LinkToPositionModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidateId: string;
  candidateName: string;
}

const LinkToPositionModal: React.FC<LinkToPositionModalProps> = ({
  isOpen,
  onClose,
  candidateId,
  candidateName,
}) => {
  const { positions, loading, addMemberToPosition } = usePositions();

  const handleLinkToPosition = async (positionId: string) => {
    await addMemberToPosition(positionId, candidateId);
    toast.success(`${candidateName} linked to position`);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Link Candidate to Position</DialogTitle>
          <DialogDescription>
            Select a position to link <span className="font-medium text-foreground">{candidateName}</span> to.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Loading positions...
            </p>
          ) : positions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No positions available. Create a position first.
            </p>
          ) : (
            positions.map((position) => {
              const isAlreadyMember = position.members.some(m => m.submission_id === candidateId);
              return (
                <Button
                  key={position.id}
                  variant="outline"
                  className="w-full justify-start h-auto py-3"
                  disabled={isAlreadyMember}
                  onClick={() => handleLinkToPosition(position.id)}
                >
                  <div className="flex items-center gap-3 w-full">
                    <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="text-left min-w-0">
                      <div className="font-medium truncate">{position.name || 'Untitled Position'}</div>
                      <div className="text-xs text-muted-foreground">
                        {position.members.length} candidate{position.members.length !== 1 ? 's' : ''}
                        {isAlreadyMember && ' • Already linked'}
                      </div>
                    </div>
                  </div>
                </Button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LinkToPositionModal;
